import { useCallback } from 'react';

import getISOWeek from 'date-fns/getISOWeek';
import getISOWeekYear from 'date-fns/getISOWeekYear';

import {
	getLocalStorageItemAsString,
	setLocalStorageItem,
} from '@atlassian/search-common/local-storage';

const COOLDOWN_STORAGE_KEY = 'quick_find_connect_apps_cooldown';
const MAX_ANIMATIONS_PER_WEEK = 10;
const DISMISSED_VALUE = 'dismissed';

type CooldownStore = {
	count: number;
	weekKey: string;
};

const getCurrentWeekKey = (): string => {
	const now = new Date();
	// ISO week-year + ISO week so we don't get an off-by-one across a year boundary
	// (e.g. Jan 1 can fall in the previous year's last ISO week).
	return `${getISOWeekYear(now)}_w${getISOWeek(now)}`;
};

const readStore = (): CooldownStore | null => {
	const raw = getLocalStorageItemAsString(COOLDOWN_STORAGE_KEY);
	if (!raw) {
		return null;
	}
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (
			parsed &&
			typeof parsed === 'object' &&
			'count' in parsed &&
			'weekKey' in parsed &&
			typeof (parsed as CooldownStore).count === 'number' &&
			typeof (parsed as CooldownStore).weekKey === 'string'
		) {
			return parsed as CooldownStore;
		}
		return null;
	} catch {
		// JSON parse error — degrade gracefully.
		return null;
	}
};

const isDismissed = (): boolean => {
	return getLocalStorageItemAsString(COOLDOWN_STORAGE_KEY) === DISMISSED_VALUE;
};

const writeStore = (store: CooldownStore): void => {
	try {
		setLocalStorageItem(COOLDOWN_STORAGE_KEY, JSON.stringify(store));
	} catch {
		// Private browsing / storage full / quota exceeded — degrade gracefully
		// (the animation plays anyway; we just lose the count for this attempt).
	}
};

/** Per-browser frequency cap for the Connect Apps animation. Resets on ISO-week rollover. */
export const useConnectAppsCooldown = (
	maxPerWeek: number = MAX_ANIMATIONS_PER_WEEK,
): { canAnimate: () => boolean; recordAnimation: () => void; dismissPermanently: () => void } => {
	const canAnimate = useCallback((): boolean => {
		if (isDismissed()) {
			return false;
		}
		const weekKey = getCurrentWeekKey();
		const stored = readStore();
		if (stored?.weekKey === weekKey && stored.count >= maxPerWeek) {
			return false;
		}
		return true;
	}, [maxPerWeek]);

	const recordAnimation = useCallback((): void => {
		const weekKey = getCurrentWeekKey();
		const stored = readStore();
		const count = stored?.weekKey === weekKey ? stored.count + 1 : 1;
		writeStore({ weekKey, count });
	}, []);

	const dismissPermanently = useCallback((): void => {
		try {
			setLocalStorageItem(COOLDOWN_STORAGE_KEY, DISMISSED_VALUE);
		} catch {
			// degrade gracefully
		}
	}, []);

	return { canAnimate, recordAnimation, dismissPermanently };
};
